import { propertyTypeOptions } from '@/shared/api';
import { I } from '@/shared/icons';
import type { WizardForm } from './wizardModel';
import { WizardField } from './WizardField';

interface StepOperationsProps {
  form: WizardForm;
  patch: (partial: Partial<WizardForm>) => void;
  errors: Record<string, string>;
}

export function StepOperations({ form, patch, errors }: StepOperationsProps) {
  const typeLabel = propertyTypeOptions.find((o) => o.value === form.propertyType)?.label;
  const location =
    form.locationMode === 'existing'
      ? form.existingObjectLabel || 'Bestehendes Objekt'
      : `${form.streetName} ${form.houseNumber}, ${form.postalCode} ${form.city}`;

  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2">
        <WizardField
          label="Grundpreis / Nacht"
          type="number"
          min={0}
          value={form.baseNightlyRate}
          error={errors.baseNightlyRate}
          onChange={(v) => patch({ baseNightlyRate: Number(v) })}
        />
        <WizardField
          label="Reinigungsgebühr"
          type="number"
          min={0}
          value={form.cleaningFee}
          error={errors.cleaningFee}
          onChange={(v) => patch({ cleaningFee: Number(v) })}
        />
        {form.locationMode === 'new' && (
          <WizardField
            label="Währung"
            value={form.currency}
            error={errors.currency}
            hint="ISO-Code, z. B. EUR — gilt für das ganze neue Objekt. Ohne Änderung gilt die des Workspace."
            onChange={(currency) => patch({ currency })}
          />
        )}
      </div>

      <div className="border-line bg-card-alt/60 flex items-start gap-3 rounded-lg border p-4">
        <I name="Info" size={18} className="text-postmark mt-0.5 shrink-0" />
        <p className="text-ink-soft text-sm">
          Das Apartment wird als <span className="text-ink font-medium">Entwurf</span> angelegt —
          noch keine Channex-Verbindung. Die Veröffentlichung startest du danach auf der Detailseite
          über den Channex-Preflight.
        </p>
      </div>

      <div className="border-line bg-card rounded-lg border p-5">
        <p className="eyebrow mb-3">Zusammenfassung</p>
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <SummaryRow label="Standort" value={location} />
          <SummaryRow label="Apartment" value={form.name || '—'} />
          <SummaryRow label="Typ" value={typeLabel ?? form.propertyType} />
          <SummaryRow
            label="Belegung"
            value={`${form.occupancyAdults + form.occupancyChildren} Schlafplätze · ${form.bedrooms} Zi.`}
          />
        </dl>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-line/70 flex justify-between gap-3 border-b border-dashed pb-1">
      <dt className="text-ink-faint">{label}</dt>
      <dd className="text-ink text-right font-medium">{value}</dd>
    </div>
  );
}
